package com.eladfinish.bible.contest.navigation.back

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState

/**
 * Intercepts the system/toolbar "Back" action while [enabled] is true.
 *
 * This is useful when you need to run custom logic *before* leaving the current screen,
 * for example:
 * - Setting a navigation result via `SavedStateHandle` (e.g., "refresh previous screen")
 * - Showing a confirmation dialog ("Discard changes?")
 * - Closing an open sheet/dialog instead of popping the back stack
 *
 * The interceptor is registered when this composable enters the composition and removed
 * automatically when it leaves the composition (or when [enabled] becomes false).
 *
 * Contract:
 * - [onBack] should execute quickly and must be side-effect safe.
 * - If [enabled] is true, the back event is considered handled (consumed) by returning `true`
 *   from the interceptor.
 *
 * Notes:
 * - This implementation relies on [LocalBackInterceptorRegistry]. Make sure the registry is
 *   provided above this screen (e.g., at the root of your navigation host / activity).
 * - If you register multiple interceptors, ensure your registry defines precedence rules.
 *
 * Example: refresh RandomVerse after closing settings
 * ```
 * InterceptBackWhen(
 *     enabled = true,
 *     onBack = {
 *         navController.previousBackStackEntry
 *             ?.savedStateHandle
 *             ?.set(KEY_RANDOM_VERSE_REFRESH, true)
 *
 *         navController.popBackStackSafely()
 *     },
 * )
 * ```
 */
@Composable
fun InterceptBackWhen(
    enabled: Boolean = true,
    onBack: () -> Unit,
) {
    val backRegistry = LocalBackInterceptorRegistry.current

    val currentOnBack by rememberUpdatedState(onBack)

    DisposableEffect(enabled) {
        if (enabled) {
            backRegistry.set {
                currentOnBack()
                true
            }
        }
        onDispose {
            backRegistry.set(null)
        }
    }
}
